import { useCallback, useEffect, useRef, useState } from 'react';
import api, { getApiError } from '../../services/api';
import type { Order } from '../../types/Order';
import { POLL_MS } from '../orders/config';
import { isActive } from '../orders/orderApi';
import type { PanelOrderDetail } from './types';

export type DetailState = 'loading' | 'error' | 'missing' | 'ready';

/** One order with its history. Polled while the order is active so new events appear without a reload. */
export function useOrderDetail(id: string | undefined) {
    const [order, setOrder] = useState<PanelOrderDetail | null>(null);
    const [state, setState] = useState<DetailState>('loading');
    const [receivedAt, setReceivedAt] = useState(0);
    const latest = useRef(0);

    const load = useCallback(
        async (initial: boolean) => {
            if (!id) return;
            const call = ++latest.current;
            if (initial) setState('loading');
            try {
                const { data } = await api.get<PanelOrderDetail>(`/orders/${id}/panel`);
                if (call !== latest.current) return;
                setOrder(data);
                setReceivedAt(Date.now());
                setState('ready');
            } catch (e) {
                if (call === latest.current && initial) setState(getApiError(e).status === 404 ? 'missing' : 'error');
            }
        },
        [id]
    );

    /** Drops answers that are still on their way (unmount, other order). */
    const discard = useCallback(() => {
        latest.current++;
    }, []);
    useEffect(() => {
        void load(true);
        return discard;
    }, [load, discard]);

    const active = order !== null && isActive(order.status);
    useEffect(() => {
        if (!active) return;
        const h = setInterval(() => {
            if (!document.hidden) void load(false);
        }, POLL_MS);
        return () => clearInterval(h);
    }, [active, load]);

    /** Optimistic local change; `reload` brings the server's version (with the new event). */
    const patch = useCallback((_id: string, change: Partial<Order>) => setOrder((o) => (o ? { ...o, ...change } : o)), []);
    const reload = useCallback(() => load(false), [load]);
    const retry = useCallback(() => load(true), [load]);

    return { order, state, receivedAt, patch, reload, retry };
}
